import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
    AccessTimeRounded,
    AddRounded,
    AttachFileRounded,
    ChatBubbleOutlineRounded,
    LabelOutlined,
    SearchRounded,
} from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    InputAdornment,
    Portal,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { colors } from "../../theme/colors";
import { toast } from "../../universal/toast";
import utils from "../../universal/utils";
import {
    ACCOUNT_PAGE_MAIN_CONTENT_SX,
    ACCOUNT_PAGE_TITLE_SX,
} from "./accountPageStyles";
import { toSettingsSectionPath } from "./accountSettingsNav";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import { useScrollLock } from "../common/lenisContext";
import SupportTicketSidebarChat from "./SupportTicketSidebarChat";
import {
    SUPPORT_TICKET_STATUS_FILTERS,
    filterSupportTickets,
    formatSupportTicketCategory,
    formatSupportTicketRelativeTime,
    getSupportTicketCardDetails,
    getSupportTicketIdFromHash,
    getSupportTicketStatusLabel,
    getSupportTicketStatusTone,
} from "./supportTicketUi";
import {
    SUPPORT_TICKET_CARD_DESCRIPTION_SX,
    SUPPORT_TICKET_CARD_META_SX,
    SUPPORT_TICKET_CARD_SUBJECT_SX,
    SUPPORT_TICKET_CARD_SX,
    SUPPORT_TICKET_CARD_TOP_SX,
    SUPPORT_TICKET_DRAWER_BACKDROP_SX,
    SUPPORT_TICKET_DRAWER_INNER_SX,
    SUPPORT_TICKET_EMPTY_ACTION_SX,
    SUPPORT_TICKET_EMPTY_ICON_SX,
    SUPPORT_TICKET_EMPTY_STATE_SX,
    SUPPORT_TICKET_META_ITEM_SX,
    SUPPORT_TICKET_NUMBER_SX,
    SUPPORT_TICKET_STATE_COPY_SX,
    SUPPORT_TICKET_STATE_SX,
    SUPPORT_TICKET_STATE_TITLE_SX,
    SUPPORT_TICKETS_COUNT_SX,
    SUPPORT_TICKETS_CONTROLS_SX,
    SUPPORT_TICKETS_FILTERS_SX,
    SUPPORT_TICKETS_GRID_SX,
    SUPPORT_TICKETS_HEADER_SX,
    SUPPORT_TICKETS_HISTORY_HEADER_SX,
    SUPPORT_TICKETS_HISTORY_TITLE_SX,
    SUPPORT_TICKETS_NEW_BUTTON_SX,
    SUPPORT_TICKETS_SEARCH_SX,
    SUPPORT_TICKETS_SECTION_SX,
    getSupportTicketDrawerSx,
    getSupportTicketStatusSx,
    getSupportTicketsFilterSx,
} from "./supportTicketsPageStyles";

const basePath = utils.getEdgeBasePath();
const supportTicketsPath = toSettingsSectionPath("support-tickets");

function SupportTicketCard({ ticket, onOpen }) {
    const statusTone = getSupportTicketStatusTone(ticket.status);
    const cardDetails = getSupportTicketCardDetails(ticket);

    return (
        <Box
            component="button"
            type="button"
            onClick={() => onOpen(ticket._id)}
            aria-label={
                "Open ticket " + ticket.ticketNumber + ": " + ticket.subject
            }
            sx={SUPPORT_TICKET_CARD_SX}
        >
            <Box sx={SUPPORT_TICKET_CARD_TOP_SX}>
                <Box component="span" sx={SUPPORT_TICKET_NUMBER_SX}>
                    {"#" + (ticket.ticketNumber || ticket._id)}
                </Box>
                <Box
                    component="span"
                    sx={getSupportTicketStatusSx(statusTone)}
                >
                    {getSupportTicketStatusLabel(ticket.status)}
                </Box>
            </Box>

            <Typography component="h3" sx={SUPPORT_TICKET_CARD_SUBJECT_SX}>
                {ticket.subject || "Support ticket"}
            </Typography>
            <Typography sx={SUPPORT_TICKET_CARD_DESCRIPTION_SX}>
                {cardDetails.description || "No description was provided."}
            </Typography>

            <Box sx={SUPPORT_TICKET_CARD_META_SX}>
                <Box component="span" sx={SUPPORT_TICKET_META_ITEM_SX}>
                    <LabelOutlined />
                    {formatSupportTicketCategory(ticket.category)}
                </Box>
                {cardDetails.attachmentCount > 0 && (
                    <Box component="span" sx={SUPPORT_TICKET_META_ITEM_SX}>
                        <AttachFileRounded />
                        {"Attachments: " + cardDetails.attachmentCount}
                    </Box>
                )}
                <Box component="span" sx={SUPPORT_TICKET_META_ITEM_SX}>
                    <ChatBubbleOutlineRounded />
                    {(ticket.messages || []).length +
                        ((ticket.messages || []).length === 1
                            ? " reply"
                            : " replies")}
                </Box>
                <Box component="span" sx={SUPPORT_TICKET_META_ITEM_SX}>
                    <AccessTimeRounded />
                    {formatSupportTicketRelativeTime(
                        ticket.updatedAt || ticket.createdAt,
                    )}
                </Box>
            </Box>
        </Box>
    );
}

SupportTicketCard.propTypes = {
    ticket: PropTypes.object.isRequired,
    onOpen: PropTypes.func.isRequired,
};

export default function SupportTicketsPage({ user, avatarCacheBust }) {
    const history = useHistory();
    const location = useLocation();
    const selectedTicketId = getSupportTicketIdFromHash(location.hash);
    const sidebarOpen = Boolean(selectedTicketId);
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);

    useScrollLock(sidebarOpen);

    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(true);
    const [listError, setListError] = useState("");
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const [ticket, setTicket] = useState(null);
    const [loadingTicket, setLoadingTicket] = useState(false);
    const [ticketError, setTicketError] = useState("");
    const [replyContent, setReplyContent] = useState("");
    const [replying, setReplying] = useState(false);

    const fetchTickets = useCallback(async () => {
        setLoadingTickets(true);
        setListError("");

        try {
            const response = await fetch(
                `${basePath}/api/v1/support/tickets/me`,
                { credentials: "include" },
            );
            const data = await response.json();
            if (!response.ok || !data?.ok) {
                throw new Error(data?.message || "Could not load tickets");
            }
            setTickets(data.tickets || []);
        } catch (error) {
            console.error("Failed to fetch support tickets", error);
            setListError(
                "We could not load your ticket history. Please try again.",
            );
        } finally {
            setLoadingTickets(false);
        }
    }, []);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    const fetchTicket = useCallback(
        async ({ quiet = false } = {}) => {
            if (!selectedTicketId) return;
            if (!quiet) setLoadingTicket(true);
            setTicketError("");

            try {
                const response = await fetch(
                    `${basePath}/api/v1/support/tickets/${selectedTicketId}`,
                    { credentials: "include" },
                );
                const data = await response.json();
                if (!response.ok || !data?.ok) {
                    throw new Error(data?.message || "Ticket not found");
                }
                setTicket(data.ticket);
            } catch (error) {
                console.error("Failed to fetch support ticket", error);
                setTicket(null);
                setTicketError("This ticket could not be loaded.");
            } finally {
                setLoadingTicket(false);
            }
        },
        [selectedTicketId],
    );

    useEffect(() => {
        if (!selectedTicketId) {
            setTicket(null);
            setTicketError("");
            setReplyContent("");
            return;
        }
        fetchTicket();
    }, [fetchTicket, selectedTicketId]);

    const filteredTickets = useMemo(
        () => filterSupportTickets(tickets, query, statusFilter),
        [query, statusFilter, tickets],
    );

    const openTicket = (ticketId) => {
        history.push({
            pathname: supportTicketsPath,
            hash: "#" + encodeURIComponent(ticketId),
        });
    };

    const closeTicket = () => {
        history.push({
            pathname: supportTicketsPath,
            hash: "",
        });
    };

    const handleReply = async () => {
        const content = replyContent.trim();
        if (!content || !selectedTicketId || replying) return;

        setReplying(true);
        try {
            const response = await fetch(
                `${basePath}/api/v1/support/tickets/${selectedTicketId}/reply`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ content }),
                },
            );
            const data = await response.json();
            if (!response.ok || !data?.ok) {
                throw new Error(data?.message || "Could not send reply");
            }

            setReplyContent("");
            toast.success("Message sent successfully");
            await Promise.all([
                fetchTicket({ quiet: true }),
                fetchTickets(),
            ]);
        } catch (error) {
            console.error("Failed to reply to support ticket", error);
            toast.error("Your message could not be sent");
        } finally {
            setReplying(false);
        }
    };

    return (
        <Box sx={ACCOUNT_PAGE_MAIN_CONTENT_SX}>
            <Box sx={SUPPORT_TICKETS_SECTION_SX}>
                <React.Fragment>
                        <Box sx={SUPPORT_TICKETS_HEADER_SX}>
                            <Typography
                                component="h1"
                                sx={ACCOUNT_PAGE_TITLE_SX}
                            >
                                Support tickets
                            </Typography>
                        </Box>

                        {tickets.length > 0 ? (
                            <Box sx={SUPPORT_TICKETS_CONTROLS_SX}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search by ticket ID or subject"
                                value={query}
                                onChange={(event) =>
                                    setQuery(event.target.value)
                                }
                                inputProps={{
                                    "aria-label": "Search support tickets",
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchRounded
                                                sx={{
                                                    color: colors.text.muted,
                                                    fontSize: "1.15rem",
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={SUPPORT_TICKETS_SEARCH_SX}
                            />
                            <Box
                                component={Link}
                                to="/support"
                                sx={SUPPORT_TICKETS_NEW_BUTTON_SX}
                            >
                                <AddRounded sx={{ fontSize: "1rem" }} />
                                New ticket
                            </Box>
                            </Box>
                        ) : null}

                        <Box sx={SUPPORT_TICKETS_HISTORY_HEADER_SX}>
                            <Typography
                                component="h2"
                                sx={SUPPORT_TICKETS_HISTORY_TITLE_SX}
                            >
                                Ticket history
                            </Typography>
                            <Typography sx={SUPPORT_TICKETS_COUNT_SX}>
                                {filteredTickets.length +
                                    (filteredTickets.length === 1
                                        ? " ticket"
                                        : " tickets")}
                            </Typography>
                        </Box>

                        {tickets.length > 0 && (
                            <Box
                                component="nav"
                                aria-label="Filter tickets by status"
                                sx={SUPPORT_TICKETS_FILTERS_SX}
                            >
                                {SUPPORT_TICKET_STATUS_FILTERS.map((status) => (
                                    <Box
                                        component="button"
                                        type="button"
                                        key={status.value}
                                        onClick={() =>
                                            setStatusFilter(status.value)
                                        }
                                        sx={getSupportTicketsFilterSx(
                                            statusFilter === status.value,
                                        )}
                                    >
                                        {status.label}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {loadingTickets ? (
                            <Box sx={SUPPORT_TICKET_STATE_SX}>
                                <CircularProgress
                                    size={28}
                                    sx={{ color: colors.primary.main }}
                                />
                                <Typography sx={SUPPORT_TICKET_STATE_COPY_SX}>
                                    Loading your ticket history…
                                </Typography>
                            </Box>
                        ) : listError ? (
                            <Box sx={SUPPORT_TICKET_STATE_SX}>
                                <Typography
                                    sx={SUPPORT_TICKET_STATE_TITLE_SX}
                                >
                                    Ticket history unavailable
                                </Typography>
                                <Typography sx={SUPPORT_TICKET_STATE_COPY_SX}>
                                    {listError}
                                </Typography>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={fetchTickets}
                                    sx={getSupportTicketsFilterSx(true)}
                                >
                                    Try again
                                </Box>
                            </Box>
                        ) : filteredTickets.length === 0 ? (
                            <Box
                                sx={
                                    tickets.length === 0
                                        ? SUPPORT_TICKET_EMPTY_STATE_SX
                                        : SUPPORT_TICKET_STATE_SX
                                }
                            >
                                <Box sx={SUPPORT_TICKET_EMPTY_ICON_SX}>
                                    <ChatBubbleOutlineRounded />
                                </Box>
                                <Typography
                                    sx={SUPPORT_TICKET_STATE_TITLE_SX}
                                >
                                    {tickets.length === 0
                                        ? "No support tickets yet"
                                        : "No tickets found"}
                                </Typography>
                                <Typography sx={SUPPORT_TICKET_STATE_COPY_SX}>
                                    {tickets.length === 0
                                        ? "When you contact Ouro Support, your conversation will appear here."
                                        : "Try another ticket ID, subject, or status filter."}
                                </Typography>
                                {tickets.length === 0 ? (
                                    <Box
                                        component={Link}
                                        to="/support"
                                        sx={SUPPORT_TICKET_EMPTY_ACTION_SX}
                                    >
                                        <AddRounded sx={{ fontSize: "1rem" }} />
                                        New ticket
                                    </Box>
                                ) : null}
                            </Box>
                        ) : (
                            <Box sx={SUPPORT_TICKETS_GRID_SX}>
                                {filteredTickets.map((item) => (
                                    <SupportTicketCard
                                        key={item._id}
                                        ticket={item}
                                        onOpen={openTicket}
                                    />
                                ))}
                            </Box>
                        )}
                </React.Fragment>
            </Box>

            <Portal>
                {sidebarOpen && !isMobile ? (
                    <Box
                        aria-hidden
                        onClick={closeTicket}
                        sx={SUPPORT_TICKET_DRAWER_BACKDROP_SX}
                    />
                ) : null}

                <Box
                    role="dialog"
                    aria-modal="true"
                    aria-label="Support ticket conversation"
                    sx={getSupportTicketDrawerSx(sidebarOpen)}
                >
                    <Box sx={SUPPORT_TICKET_DRAWER_INNER_SX}>
                        {sidebarOpen ? (
                            <SupportTicketSidebarChat
                                ticket={ticket}
                                loading={loadingTicket}
                                error={ticketError}
                                replyContent={replyContent}
                                replying={replying}
                                user={user}
                                avatarCacheBust={avatarCacheBust}
                                onReplyChange={setReplyContent}
                                onSendReply={handleReply}
                                onBack={closeTicket}
                            />
                        ) : null}
                    </Box>
                </Box>
            </Portal>
        </Box>
    );
}

SupportTicketsPage.propTypes = {
    user: PropTypes.object,
    avatarCacheBust: PropTypes.number,
};
